import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { registerUser, checkUsernameAvailability } from "../../api";
import { toast, Toaster } from "react-hot-toast";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { 
  UserIcon, 
  LockClosedIcon,
  PhoneIcon,
  MapPinIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  BookOpenIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  UserCircleIcon,
  IdentificationIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

const Register = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    username: "",
    password: "",
    parentNumber: "",
    contactNo: "",
    address: "",
    userClass: "6th",
    schoolName: "",
    course: "",
    dateOfExam: "",
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userInfo, setUserInfo] = useState({ userId: "", username: "" });
  const [loading, setLoading] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState({ checking: false, available: null, message: "" });

  const navigate = useNavigate();

  // Auto-generate unique username from full name
  useEffect(() => {
    const generateUniqueUsername = async () => {
      if (formData.fullName.trim()) {
        setUsernameStatus({ checking: true, available: null, message: "Generating..." });
        
        // Convert full name to username format
        const baseUsername = formData.fullName
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '_')
          .replace(/[^a-z0-9_]/g, '');
        
        if (baseUsername.length < 3) {
          setFormData(prev => ({ ...prev, username: "" }));
          setUsernameStatus({ checking: false, available: false, message: "Name too short" });
          return;
        }

        // Check if base username is available
        try {
          let finalUsername = baseUsername;
          let isAvailable = false;
          let attempts = 0;
          const maxAttempts = 10;

          // Try base username first
          const response = await checkUsernameAvailability(finalUsername);
          
          if (response.available) {
            isAvailable = true;
          } else {
            // If not available, add random numbers until we find a unique one
            while (!isAvailable && attempts < maxAttempts) {
              const randomSuffix = Math.floor(1000 + Math.random() * 9000); // 4-digit number
              finalUsername = `${baseUsername}${randomSuffix}`;
              
              const checkResponse = await checkUsernameAvailability(finalUsername);
              if (checkResponse.available) {
                isAvailable = true;
              }
              attempts++;
            }
          }

          if (isAvailable) {
            setFormData(prev => ({ ...prev, username: finalUsername }));
            setUsernameStatus({ 
              checking: false, 
              available: true, 
              message: finalUsername === baseUsername ? "Available" : "Made unique with number" 
            });
          } else {
            setFormData(prev => ({ ...prev, username: "" }));
            setUsernameStatus({ checking: false, available: false, message: "Unable to generate" });
          }
        } catch (error) {
          console.error("Error checking username:", error);
          // Fallback: add timestamp if API fails
          const fallbackUsername = `${baseUsername}${Date.now().toString().slice(-4)}`;
          setFormData(prev => ({ ...prev, username: fallbackUsername }));
          setUsernameStatus({ checking: false, available: true, message: "Generated" });
        }
      } else {
        setFormData(prev => ({ ...prev, username: "" }));
        setUsernameStatus({ checking: false, available: null, message: "" });
      }
    };

    // Debounce the username generation
    const timeoutId = setTimeout(() => {
      generateUniqueUsername();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.fullName]);

  const validateForm = () => {
    const newErrors = {};
    const { fullName, username, password, parentNumber, contactNo, address, userClass, schoolName, course, dateOfExam } = formData;

    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (fullName.trim().length < 3) {
      newErrors.fullName = "Name must be at least 3 characters";
    } else if (!/^[a-zA-Z\s]+$/.test(fullName.trim())) {
      newErrors.fullName = "Name can only contain letters";
    }

    if (!username.trim()) {
      newErrors.username = "Username is required";
    } else if (username.trim().length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    } else if (usernameStatus.available === false) {
      newErrors.username = "Username not available";
    }

    if (!password.trim()) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    } else if (!/(?=.*[a-z])(?=.*[A-Z])/.test(password)) {
      newErrors.password = "Password must contain uppercase and lowercase letters";
    }

    if (!parentNumber) {
      newErrors.parentNumber = "Parent number is required";
    } else if (!parentNumber.match(/^\d{10}$/)) {
      newErrors.parentNumber = "Must be exactly 10 digits";
    }

    if (!contactNo) {
      newErrors.contactNo = "Contact number is required";
    } else if (!contactNo.match(/^\d{10}$/)) {
      newErrors.contactNo = "Must be exactly 10 digits";
    } else if (contactNo === parentNumber) {
      newErrors.contactNo = "Contact and parent numbers cannot be same";
    }

    if (!address.trim()) {
      newErrors.address = "Address is required";
    } else if (address.trim().length < 4) {
      newErrors.address = "Address must be at least 5 characters";
    }

    if (!userClass) {
      newErrors.userClass = "Class is required";
    }

    if (!schoolName.trim()) {
      newErrors.schoolName = "School name is required";
    } else if (schoolName.trim().length < 3) {
      newErrors.schoolName = "School name must be at least 3 characters";
    }

    if (!course) {
      newErrors.course = "Course selection is required";
    }

    if (!dateOfExam) {
      newErrors.dateOfExam = "Exam date is required";
    } else {
      const selectedDate = new Date(dateOfExam);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.dateOfExam = "Exam date cannot be in the past";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix all errors before submitting");
      return;
    }
    
    if (usernameStatus.checking) {
      toast.error("Please wait while we generate your username");
      return;
    }

    setLoading(true);
    try {
      const response = await registerUser(formData);
      if (response.message === "User registered successfully") {
        setUserInfo({ userId: response.user_id, username: response.username });
        setIsModalOpen(true);
        toast.success("Registration successful!");
      } else {
        toast.error(response.message || "Registration failed");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(error.message || "Failed to register user");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'parentNumber' || name === 'contactNo') {
      const cleanedValue = value.replace(/\D/g, '');
      if (cleanedValue.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
      }
    } else if (name === 'fullName') {
      const cleanedValue = value.replace(/[^a-zA-Z\s]/g, '');
      setFormData((prev) => ({ ...prev, [name]: cleanedValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const img = new Image();
    img.src = "/assets/bg-rcat.jpg";
    img.onload = () => {
      doc.addImage(img, "PNG", 10, 10, 190, 70);
      doc.setFontSize(20);
      doc.text("User Registration Details", 50, 90);
      doc.autoTable({
        startY: 100,
        head: [["Field", "Details"]],
        body: [
          ["User ID", userInfo.userId],
          ["Full Name", formData.fullName],
          ["Username", formData.username],
          ["Password", formData.password],
          ["Class", formData.userClass],
          ["Course", formData.course],
          ["Exam Date", formData.dateOfExam],
        ],
      });
      doc.save("RCAT_Registration_Details.pdf");
      toast.success("PDF downloaded successfully!");
      closeModal();
    };
    img.onerror = () => {
      toast.error("Failed to load banner image");
      closeModal();
    };
  };

  const closeModal = () => {
    setIsModalOpen(false);
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      <Toaster position="top-right" toastOptions={{
        style: { background: '#fff', color: '#363636', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', borderRadius: '12px' },
        duration: 4000
      }} />
      
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-indigo-200/40 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center p-3 sm:p-4 lg:p-6">
        <div className="w-full max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 bg-white rounded-2xl shadow-2xl overflow-hidden">
            
            {/* Left Side - Image */}
            <div className="hidden lg:block relative">
              <img src="/assets/bg-cat.jpg" alt="Registration Banner" className="w-full h-full object-cover" />
            </div>

            {/* Right Side - Form */}
            <div className="flex flex-col bg-white">
              {/* Header */}
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-2.5">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
                    <UserCircleIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-gray-800">RCAT Registration</h1>
                    <p className="text-xs text-gray-500">Create your account</p>
                  </div>
                </div>
              </div>

              {/* Form - Scrollable */}
              <div className="flex-1 overflow-y-auto px-5 py-4">
                <form onSubmit={handleRegister} className="space-y-3">
                  
                  {/* Full Name and Username in Same Row */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <IdentificationIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input 
                          type="text" 
                          name="fullName" 
                          className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.fullName ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} 
                          value={formData.fullName} 
                          onChange={handleChange} 
                          placeholder="Your full name" 
                        />
                      </div>
                      {errors.fullName && <p className="mt-1 text-xs text-red-600">⚠ {errors.fullName}</p>}
                    </div>

                    {/* Username (Auto-generated, Read-only) */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center">
                        Username 
                        <span className="text-blue-600 ml-1 text-[10px]">(Auto-generated)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <UserIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input 
                          type="text" 
                          name="username" 
                          className={`w-full pl-10 pr-10 py-2.5 text-sm border-2 rounded-lg cursor-not-allowed transition-all ${
                            usernameStatus.checking 
                              ? 'bg-yellow-50 border-yellow-300 text-yellow-700' 
                              : usernameStatus.available 
                                ? 'bg-green-50 border-green-300 text-green-700' 
                                : usernameStatus.available === false 
                                  ? 'bg-red-50 border-red-300 text-red-700'
                                  : 'bg-gray-100 border-gray-200 text-gray-600'
                          }`}
                          value={formData.username || (usernameStatus.checking ? 'Generating...' : 'username')} 
                          readOnly 
                          title="Username is auto-generated from your name"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          {usernameStatus.checking ? (
                            <div className="w-4 h-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                          ) : usernameStatus.available ? (
                            <CheckBadgeIcon className="h-4 w-4 text-green-600" />
                          ) : usernameStatus.available === false ? (
                            <ExclamationCircleIcon className="h-4 w-4 text-red-600" />
                          ) : null}
                        </div>
                      </div>
                      {usernameStatus.message && (
                        <p className={`mt-1 text-xs ${
                          usernameStatus.available ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {usernameStatus.available && '✓'} {usernameStatus.message}
                        </p>
                      )}
                      {errors.username && <p className="mt-1 text-xs text-red-600">⚠ {errors.username}</p>}
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockClosedIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <input type={showPassword ? "text" : "password"} name="password" className={`w-full pl-10 pr-10 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.password ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.password} onChange={handleChange} placeholder="Create a strong password" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        {showPassword ? <EyeSlashIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" /> : <EyeIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />}
                      </button>
                    </div>
                    {errors.password && <p className="mt-1 text-xs text-red-600">⚠ {errors.password}</p>}
                  </div>

                  {/* Phone Numbers */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Parent Number <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <PhoneIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type="tel" name="parentNumber" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.parentNumber ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.parentNumber} onChange={handleChange} placeholder="10-digit" maxLength={10} inputMode="numeric" />
                      </div>
                      {errors.parentNumber && <p className="mt-1 text-xs text-red-600">⚠ {errors.parentNumber}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Contact Number <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <PhoneIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type="tel" name="contactNo" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.contactNo ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.contactNo} onChange={handleChange} placeholder="10-digit" maxLength={10} inputMode="numeric" />
                      </div>
                      {errors.contactNo && <p className="mt-1 text-xs text-red-600">⚠ {errors.contactNo}</p>}
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Address <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <div className="absolute top-2.5 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPinIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      <textarea name="address" rows="2" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none ${errors.address ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.address} onChange={handleChange} placeholder="Enter complete address" />
                    </div>
                    {errors.address && <p className="mt-1 text-xs text-red-600">⚠ {errors.address}</p>}
                  </div>

                  {/* Class and School */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Class <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <AcademicCapIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <select name="userClass" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer ${errors.userClass ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.userClass} onChange={handleChange}>
                          <option value="6th">6th</option>
                          <option value="7th">7th</option>
                          <option value="8th">8th</option>
                          <option value="9th">9th</option>
                          <option value="10th">10th</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">School Name <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type="text" name="schoolName" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.schoolName ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.schoolName} onChange={handleChange} placeholder="School name" />
                      </div>
                      {errors.schoolName && <p className="mt-1 text-xs text-red-600">⚠ {errors.schoolName}</p>}
                    </div>
                  </div>

                  {/* Course and Date */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Course <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <BookOpenIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <select name="course" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer ${errors.course ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.course} onChange={handleChange}>
                          <option value="">Select</option>
                          <option value="JEE">JEE</option>
                          <option value="NEET">NEET</option>
                          <option value="NDA">NDA</option>
                          <option value="MHT-CET">MHT-CET</option>
                          <option value="11th & 12th Boards">11th & 12th Boards</option>
                          <option value="8th to 10th Foundations">8th to 10th</option>
                        </select>
                      </div>
                      {errors.course && <p className="mt-1 text-xs text-red-600">⚠ Required</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Exam Date <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CalendarDaysIcon className="h-4 w-4 text-gray-400" />
                        </div>
                        <input type="date" name="dateOfExam" className={`w-full pl-10 pr-3 py-2.5 text-sm bg-gray-50 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.dateOfExam ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`} value={formData.dateOfExam} onChange={handleChange} min={new Date().toISOString().split('T')[0]} />
                      </div>
                      {errors.dateOfExam && <p className="mt-1 text-xs text-red-600">⚠ {errors.dateOfExam}</p>}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="pt-2 space-y-2">
                    <button type="submit" disabled={loading || usernameStatus.checking} className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm">Registering...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="h-5 w-5" />
                          <span className="text-sm">Create Account</span>
                        </>
                      )}
                    </button>
                    <button type="button" onClick={() => navigate('/login')} className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 hover:scale-[1.01] active:scale-[0.99] transition-all border border-gray-300">
                      <span className="text-sm">Already registered? <span className="text-blue-600 font-bold">Sign In</span></span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal - Same as before */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative">
            <button onClick={closeModal} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-all">
              <XMarkIcon className="h-5 w-5" />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircleIcon className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">Success! 🎉</h2>
              <p className="text-xs text-gray-600 mb-4">Account created successfully</p>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 text-left border border-blue-100">
                <div className="space-y-2">
                
                  <div className="flex justify-between items-center py-1.5 border-b border-blue-200">
                    <span className="text-xs font-semibold text-gray-600">Full Name</span>
                    <span className="text-xs font-bold text-gray-800 break-all">{formData.fullName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-blue-200">
                    <span className="text-xs font-semibold text-gray-600">Username</span>
                    <span className="text-xs font-bold text-gray-800 break-all">{formData.username}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-xs font-semibold text-gray-600">Password</span>
                    <span className="text-xs font-bold text-gray-800 break-all">{formData.password}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <button onClick={downloadPDF} className="w-full flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all">
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span className="text-sm">Download PDF</span>
                </button>
                <button onClick={closeModal} className="w-full bg-gray-100 text-gray-700 px-6 py-2.5 rounded-lg font-semibold hover:bg-gray-200 hover:scale-[1.01] active:scale-[0.99] transition-all">
                  <span className="text-sm">Continue to Login</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes blob { 0%, 100% { transform: translate(0, 0) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        .animate-blob { animation: blob 7s infinite; }
        .animation-delay-2000 { animation-delay: 2s; }
        .animation-delay-4000 { animation-delay: 4s; }
      `}</style>
    </div>
  );
};

export default Register;
